import { EntityLinks, Goal, ID } from '@griply/types';

import { getArchivedGoalIds } from '../derived-goal-state';
import {
  getPresentParentIds,
  hasUnrepresentedParent,
} from './get-present-parent-ids';

export type GoalTreeNode = {
  goal: Goal;
  children: GoalTreeNode[];
};

export type BuildGoalTreeOptions = {
  hideArchived?: boolean;
  hideCompleted?: boolean;
  // Unfiltered set: inherited state resolves against it so a sub-goal keeps it when a filter drops its parent.
  allGoals?: Goal[];
  links: EntityLinks;
};

export const buildGoalTree = (
  goals: Goal[],
  { hideArchived, hideCompleted, allGoals, links }: BuildGoalTreeOptions,
): GoalTreeNode[] => {
  const archivedIds = getArchivedGoalIds(allGoals ?? goals, { links });
  const goalMap = new Map<ID, Goal>();
  const childrenMap = new Map<ID, Goal[]>();

  for (const goal of goals) {
    goalMap.set(goal.id, goal);
  }

  const isPresent = (id: ID) => goalMap.has(id);
  const presentParentOptions = { links, goalsById: goalMap };

  for (const goal of goals) {
    if (hideArchived && archivedIds.has(goal.id)) {
      continue;
    }
    if (hideCompleted && goal.completedAt) {
      continue;
    }

    // A goal is a child of EVERY present parent, so it appears under each.
    getPresentParentIds(goal, isPresent, presentParentOptions).forEach(
      (parentId) => {
        if (!childrenMap.has(parentId)) {
          childrenMap.set(parentId, []);
        }
        childrenMap.get(parentId)!.push(goal);
      },
    );
  }

  // `visited` guards against a corrupt parent cycle sending the walk infinite.
  const buildNode = (
    goal: Goal,
    visited: Set<ID> = new Set(),
  ): GoalTreeNode => {
    if (visited.has(goal.id)) {
      return { goal, children: [] };
    }
    const nextVisited = new Set(visited).add(goal.id);

    const children = (childrenMap.get(goal.id) || [])
      .filter((child) => {
        if (hideArchived && archivedIds.has(child.id)) {
          return false;
        }
        return !hideCompleted || !child.completedAt;
      })
      .map((child) => buildNode(child, nextVisited));

    return { goal, children };
  };

  const rootGoals: Goal[] = [];
  // A goal missing a parent from the set (e.g. the caller's excluded focal goal) is ALSO a root, listed first.
  const alsoRootGoals: Goal[] = [];

  goals.forEach((goal) => {
    if (hideArchived && archivedIds.has(goal.id)) {
      return;
    }
    if (hideCompleted && goal.completedAt) {
      return;
    }

    if (!getPresentParentIds(goal, isPresent, presentParentOptions).length) {
      rootGoals.push(goal);
      return;
    }
    if (hasUnrepresentedParent(goal, isPresent, presentParentOptions)) {
      alsoRootGoals.push(goal);
    }
  });

  return [...alsoRootGoals, ...rootGoals].map((goal) => buildNode(goal));
};
